import { useState, useMemo, useEffect } from "react"
import { Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { SiteHeader } from "@/components/site-header"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { usePermissions } from "@/hooks/use-permissions"
import { usePersistedState } from "@/lib/use-persisted-state"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import {
  Dialog, DialogClose, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import {
  Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle,
} from "@/components/ui/sheet"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { SearchableSelect } from "@/components/ui/searchable-select"
import { Pagination } from "@/components/ui/pagination"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  SearchIcon, PlusIcon, PencilIcon, Trash2Icon,
  RotateCcwIcon as RestoreIcon, ArrowRightIcon,
  DownloadIcon, TruckIcon, XIcon, ChevronUpIcon, ChevronDownIcon, ChevronsUpDownIcon, CalculatorIcon, BanknoteIcon, MoreVerticalIcon, CircleCheckIcon, ReceiptIcon, GitMergeIcon, HomeIcon, CalendarIcon,
  RouteIcon, TriangleAlertIcon,
} from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import { avatarInitials, avatarColor } from "@/lib/avatar"
import { nextTravelAdvance, travelAdvanceLabel, travelAdvanceColor, travelAdvanceHover, TRAVEL_ADVANCE_OPTIONS, normalize as normalizeTravelAdvance, type TravelAdvance } from "@/lib/travel-advance"
import { useDriversSearch } from "@/hooks/use-drivers-search"
import { useInterconnect } from "@/hooks/use-interconnect"
import { InterconnectDialog } from "@/components/interconnect-dialog"
import { useReturnToBase } from "@/hooks/use-return-to-base"
import { ReturnToBaseDialog } from "@/components/return-to-base-dialog"
import { useMobileEditor, MobileEditorPanel, MobileEditorConfirmDialog } from "@/hooks/use-mobile-editor"
import { buildTripResourceOptions } from "./trip-resource-options"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { parseCSVRow, escapeCSVCell, escapeHTML } from "@/lib/csv"

// ─── Types ────────────────────────────────────────────────────────────────────

type TripStatus = "pendiente" | "en_curso" | "completado" | "cancelado"

const STATUS_LABELS: Record<TripStatus, string> = {
  pendiente:  "Pendiente",
  en_curso:   "En curso",
  completado: "Completado",
  cancelado:  "Cancelado",
}



const STATUS_COLORS: Record<TripStatus, string> = {
  pendiente:  "bg-muted text-muted-foreground",
  en_curso:   "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  completado: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  cancelado:  "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
}

const ALL_STATUSES: TripStatus[] = ["pendiente", "en_curso", "completado", "cancelado"]

import { type BillingStatus, BILLING_STATUS_LABELS, BILLING_STATUS_COLORS, ALL_BILLING_STATUSES } from "@/lib/billing-status"

interface TripRow {
  id:                    string
  internalId:            number
  shipmentId:            string | null
  shipmentInternalId:    number | null
  mobileId:              string | null
  driverId:              string | null
  truckId:               string | null
  trailerId:             string | null
  driverName:            string | null
  truckLicensePlate:     string | null
  truckUnitNumber:       number | null
  trailerLicensePlate:   string | null
  trailerUnitNumber:     number | null
  operatorName:          string | null
  originCityId:          string | null
  originCityName:        string | null
  destinationCityId:     string | null
  destinationCityName:   string | null
  departureAt:           string | null
  arrivalAt:             string | null
  routeKm:               number | null
  status:                TripStatus
  billingStatus:         "controlado" | "liquidado" | "facturado" | "cerrado" | null
  notes:                 string | null
  hasTravelAdvance:      "viaje" | "gnc" | "viaje_gnc" | null
  deliveryNoteNumber:             string | null
  scheduledOriginArrivalAt:        string | null
  deletedAt:                       string | null
  createdAt:                       string
}

interface MobileOption {
  id:                string
  isActive:          boolean
  driverId:          string | null
  truckId:           string | null
  trailerId:         string | null
  driverName:        string | null
  truckLicensePlate: string | null
  truckUnitNumber:   number | null
  operatorName:      string | null
}

interface DriverOption {
  id:   string
  name: string
  code: string
}

interface UnitOption {
  id:           string
  licensePlate: string | null
  unitNumber:   number | null
  unitTypeCode: string
}

interface CityOption { id: string; name: string; code: string }

type FilterStatus = "active" | "deleted" | "all"

const EMPTY_FORM = {
  shipmentId:        "",
  mobileId:          "",
  driverId:          "",
  truckId:           "",
  trailerId:         "",
  originCityId:      "",
  destinationCityId: "",
  departureAt:       "",
  arrivalAt:         "",
  status:            "pendiente" as TripStatus,
  billingStatus:     null as "controlado" | "liquidado" | "facturado" | "cerrado" | null,
  notes:             "",
  hasTravelAdvance:  null as "viaje" | "gnc" | "viaje_gnc" | null,
}
type FormState = typeof EMPTY_FORM

const PAGE_SIZE_OPTIONS = [25, 50, 100]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function isoToLocal(iso: string | null | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function mobileLabel(m: MobileOption): string {
  const parts = [m.truckLicensePlate, m.driverName].filter(Boolean)
  return parts.length ? parts.join(" — ") : `Móvil ${m.id.slice(0, 6)}`
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TripsPage() {
  const { can } = usePermissions()
  const qc      = useQueryClient()

  const [search,       setSearch]       = useState("")
  const [page,         setPage]         = useState(1)
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active")
  const [filterTripStatus, setFilterTripStatus] = useState<TripStatus | "">("")
  const [filterDriver,          setFilterDriver]          = usePersistedState("trips:filterDriver", "")
  const [filterBilling,         setFilterBilling]         = useState<BillingStatus | "all">("all")
  const [filterDateFrom,        setFilterDateFrom]        = useState("")
  const [filterDateTo,          setFilterDateTo]          = useState("")
  const [filterOperatorNames,   setFilterOperatorNames]   = useState<string[]>([])
  const [filterHasShipment,     setFilterHasShipment]     = useState<"all" | "with" | "without">("all")
  const [sheetOpen,    setSheetOpen]    = useState(false)
  const [editing,      setEditing]      = useState<TripRow | null>(null)
  const [editResources, setEditResources] = useState(false)
  const [form,         setForm         ] = useState<FormState>({ ...EMPTY_FORM })
  const [formError,    setFormError    ] = useState<string | null>(null)
  const [deleteOpen,   setDeleteOpen]   = useState(false)
  const [deleting,     setDeleting]     = useState<TripRow | null>(null)
  const [restoringId,  setRestoringId]  = useState<string | null>(null)
  const [pageSize,        setPageSize]        = useState(25)
  const [selectedIds,     setSelectedIds]     = useState<Set<string>>(new Set())
  const [tripExportPreview, setTripExportPreview] = useState<{ csvText: string; filename: string } | null>(null)
  const [bulkStatusPending,  setBulkStatusPending]  = useState(false)
  const [sortField, setSortField] = useState<"internalId" | "departureAt">("departureAt")
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string
    description: string
    onConfirm: () => void
  } | null>(null)
  const [sortDir,   setSortDir]   = useState<"asc" | "desc">("desc")
  const [pendingReturnToBaseId, setPendingReturnToBaseId] = useState<string | null>(null)
  const [unitSearch, setUnitSearch]           = useState("")
  const [unitSearchDebounced, setUnitSearchDebounced] = useState("")

  useEffect(() => {
    const t = setTimeout(() => setUnitSearchDebounced(unitSearch), 300)
    return () => clearTimeout(t)
  }, [unitSearch])

  function toggleSort(field: "internalId" | "departureAt") {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDir("desc")
    }
  }

  useEffect(() => { setPage(1) }, [search, filterStatus, filterTripStatus, filterDriver, filterBilling, filterDateFrom, filterDateTo, filterOperatorNames, filterHasShipment, sortField, sortDir, pageSize])

  const dateFromParam = filterDateFrom
  const dateToParam   = filterDateTo

  const { data: tripsResponse, isLoading } = useQuery({
    queryKey:        ["trips", filterStatus, filterTripStatus, filterDriver, filterBilling, dateFromParam, dateToParam, search, filterOperatorNames, filterHasShipment, sortField, sortDir, page, pageSize],
    refetchInterval: 10_000,
    queryFn:  async () => {
      const params = new URLSearchParams()
      if (filterStatus === "active")  params.set("deleted", "false")
      if (filterStatus === "deleted") params.set("deleted", "true")
      if (filterTripStatus) params.set("status", filterTripStatus)
      if (filterDriver) params.set("driverName", filterDriver)
      if (filterBilling !== "all") params.set("billingStatus", filterBilling)
      if (dateFromParam) params.set("dateFrom", dateFromParam)
      if (dateToParam)   params.set("dateTo",   dateToParam)
      if (search.trim()) params.set("search", search.trim())
      if (filterOperatorNames.length > 0) params.set("operatorName", filterOperatorNames.join(","))
      if (filterHasShipment === "with")    params.set("hasShipment", "true")
      if (filterHasShipment === "without") params.set("hasShipment", "false")
      params.set("sortBy",   sortField === "internalId" ? "internalId" : "originArrival")
      params.set("sortDir",  sortDir)
      params.set("page",     String(page))
      params.set("pageSize", String(pageSize))
      const res = await authorizedFetch(
        `${API_URL}/api/trips?${params}`,
      )
      return res.json() as Promise<{ data: TripRow[]; total: number }>
    },
  })
  const tripsData   = tripsResponse?.data  ?? []
  const serverTotal = tripsResponse?.total ?? 0

  const { data: mobileOptions = [] } = useQuery({
    queryKey: ["mobiles-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/mobiles`)
      return res.ok ? (res.json() as Promise<MobileOption[]>) : []
    },
    enabled:   sheetOpen,
    staleTime: 60_000,
  })

  const { data: cityOptions = [] } = useQuery({
    queryKey: ["cities-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/geo/cities?deleted=false`)
      return res.ok ? (res.json() as Promise<CityOption[]>) : []
    },
    enabled:   sheetOpen,
    staleTime: 60_000,
  })

  const {
    drivers: driverFilterOptions,
    onSearchChange: onDriverSearchChange,
    inputValue: driverInputValue,
    isMinLength: driverIsMinLength,
  } = useDriversSearch()

  const { data: driverList = [] } = useQuery({
    queryKey: ["drivers-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/providers/drivers?deleted=false&pageSize=500`)
      if (!res.ok) return []
      const json = await res.json() as { data: DriverOption[] }
      return json.data
    },
    enabled:   sheetOpen,
    staleTime: 60_000,
  })

  const { data: operatorList = [] } = useQuery({
    queryKey: ["operators-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/users/operators`)
      if (!res.ok) return []
      return res.json() as Promise<{ id: string; name: string }[]>
    },
    staleTime: 5 * 60_000,
  })

  const { data: appConfig } = useQuery({
    queryKey: ["app-config"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/app-config`)
      return res.ok ? (res.json() as Promise<{ trips: { basePartyId: string; baseCityId: string | null; baseCityName: string | null; averageSpeedKmh: number } }>) : null
    },
    staleTime: 5 * 60_000, // config rarely changes
  })

  const { data: unitList = [] } = useQuery({
    queryKey: ["units-select", unitSearchDebounced],
    queryFn:  async () => {
      const params = new URLSearchParams({ deleted: "false", pageSize: "500" })
      if (unitSearchDebounced.trim()) params.set("search", unitSearchDebounced.trim())
      const res = await authorizedFetch(`${API_URL}/api/units?${params}`)
      if (!res.ok) return []
      const body = await res.json() as { data: UnitOption[]; total: number }
      return body.data ?? []
    },
    enabled:   sheetOpen,
    staleTime: 60_000,
  })

  // Silently copy driver/truck/trailer from the selected mobile into the trip fields
  useEffect(() => {
    if (!form.mobileId || !sheetOpen) return
    const mobile = mobileOptions.find(m => m.id === form.mobileId)
    if (!mobile) return
    setForm(f => ({
      ...f,
      driverId:  mobile.driverId  ?? f.driverId,
      truckId:   mobile.truckId   ?? f.truckId,
      trailerId: mobile.trailerId ?? f.trailerId,
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.mobileId, mobileOptions, sheetOpen])

  // ── Filtering ─────────────────────────────────────────────────────────────

  // Derive unique operator names from the full operatorList (server-fetched once).
  // Uses the full operator list instead of paginated trip data to avoid showing only current-page operators.
  const uniqueOperatorNames = useMemo(
    () => operatorList.map(o => o.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [operatorList],
  )

  const totalPages = Math.max(1, Math.ceil(serverTotal / pageSize))

  const allSelected  = tripsData.length > 0 && tripsData.every(t => selectedIds.has(t.id))
  const someSelected = tripsData.some(t => selectedIds.has(t.id)) && !allSelected

  function toggleSelectAll() {
    setSelectedIds(prev => {
      const next = new Set(prev)
      allSelected ? tripsData.forEach(t => next.delete(t.id)) : tripsData.forEach(t => next.add(t.id))
      return next
    })
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // ── Select options ────────────────────────────────────────────────────────

  const mobileEditor = useMobileEditor({
    mobileId:      form.mobileId,
    mobileOptions,
    driverList,
    unitList,
    onError:       (msg) => setFormError(msg),
    onUnitSearch:  setUnitSearch,
  })

  const citySelectOptions = useMemo(() => [
    { value: "__none__", label: "Sin ciudad" },
    ...cityOptions.map(c => ({ value: c.id, label: c.name, sublabel: c.code })),
  ], [cityOptions])

  const driverSelectOptions = useMemo(() => [
    { value: "__none__", label: "Sin chofer" },
    ...driverList.map(d => ({ value: d.id, label: d.name, sublabel: d.code })),
  ], [driverList])

  const truckSelectOptions = useMemo(
    () => buildTripResourceOptions(
      unitList,
      ["C", "TRACTORGNC"],
      "Sin camión",
      editing
        ? { id: editing.truckId, licensePlate: editing.truckLicensePlate, unitNumber: editing.truckUnitNumber }
        : null,
      "Camión actual",
    ),
    [unitList, editing],
  )

  const trailerSelectOptions = useMemo(
    () => buildTripResourceOptions(
      unitList,
      ["S", "BATEA", "BIVUELCO", "PLAYO"],
      "Sin semi",
      editing
        ? { id: editing.trailerId, licensePlate: editing.trailerLicensePlate, unitNumber: editing.trailerUnitNumber }
        : null,
      "Semi actual",
    ),
    [unitList, editing],
  )

  // ── Mutations ─────────────────────────────────────────────────────────────

  function invalidate() { qc.invalidateQueries({ queryKey: ["trips"] }) }

  function buildPayload(f: FormState) {
    return {
      shipmentId:        f.shipmentId        || null,
      mobileId:          f.mobileId          || null,
      driverId:          f.driverId          || null,
      truckId:           f.truckId           || null,
      trailerId:         f.trailerId         || null,
      originCityId:      f.originCityId      || null,
      destinationCityId: f.destinationCityId || null,
      departureAt:       f.departureAt ? new Date(f.departureAt).toISOString() : null,
      arrivalAt:         f.arrivalAt   ? new Date(f.arrivalAt).toISOString()   : null,
      status:            f.status,
      billingStatus:     f.billingStatus ?? null,
      notes:             f.notes || null,
      hasTravelAdvance:  f.hasTravelAdvance,
    }
  }

  const createMut = useMutation({
    mutationFn: async (f: FormState) => {
      const res = await authorizedFetch(`${API_URL}/api/trips`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(buildPayload(f)),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al crear")
      }
      return res.json()
    },
    onSuccess: () => { invalidate(); toast.success("Viaje creado"); closeSheet() },
    onError:   (err: Error) => setFormError(err.message),
  })

  const updateMut = useMutation({
    mutationFn: async (f: FormState) => {
      if (!editing) throw new Error("Sin viaje seleccionado")
      const { shipmentId: _omit, ...patch } = buildPayload(f)
      const res = await authorizedFetch(`${API_URL}/api/trips/${editing.id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(patch),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar")
      }
      return res.json()
    },
    onSuccess: () => { invalidate(); toast.success("Viaje actualizado"); closeSheet() },
    onError:   (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await authorizedFetch(`${API_URL}/api/trips/${id}`, { method: "DELETE" })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar")
      }
    },
    onSuccess: () => {
      invalidate(); toast.success("Viaje eliminado")
      setDeleteOpen(false); setDeleting(null)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      setRestoringId(id)
      try {
        const res = await authorizedFetch(`${API_URL}/api/trips/${id}/restore`, { method: "PATCH" })
        if (!res.ok) {
          const body = await res.json() as { error?: string }
          throw new Error(body.error ?? "Error al restaurar")
        }
      } finally { setRestoringId(null) }
    },
    onSuccess: () => { invalidate(); toast.success("Viaje restaurado") },
    onError:   (err: Error) => toast.error(err.message),
  })

  // ── Helpers ───────────────────────────────────────────────────────────────

  function closeSheet() {
    setSheetOpen(false); setEditing(null); setEditResources(false)
    setForm({ ...EMPTY_FORM }); setFormError(null)
    mobileEditor.reset()
    setUnitSearch("")
    setUnitSearchDebounced("")
  }

  function openEdit(t: TripRow) {
    setEditing(t)
    setEditResources(false)
    setForm({
      shipmentId:        t.shipmentId        ?? "",
      mobileId:          t.mobileId          ?? "",
      driverId:          t.driverId          ?? "",
      truckId:           t.truckId           ?? "",
      trailerId:         t.trailerId         ?? "",
      originCityId:      t.originCityId      ?? "",
      destinationCityId: t.destinationCityId ?? "",
      departureAt:       isoToLocal(t.departureAt),
      arrivalAt:         isoToLocal(t.arrivalAt),
      status:            t.status,
      billingStatus:     t.billingStatus ?? null,
      notes:             t.notes ?? "",
      hasTravelAdvance:  normalizeTravelAdvance(t.hasTravelAdvance),
    })
    setFormError(null)
    mobileEditor.reset()
    setSheetOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (mobileEditor.wasEdited) {
      mobileEditor.openConfirm()
    } else {
      doSave(form)
    }
  }

  function doSave(f: FormState) {
    if (editing) updateMut.mutate(f)
    else         createMut.mutate(f)
  }

  function confirmThen(title: string, description: string, onConfirm: () => void) {
    setConfirmDialog({ title, description, onConfirm })
  }

  async function patchTripStatus(id: string, payload: { status?: TripStatus; billingStatus?: "controlado" | "liquidado" | "facturado" | "cerrado" | null }) {
    await authorizedFetch(`${API_URL}/api/trips/${id}`, {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(payload),
    })
    qc.invalidateQueries({ queryKey: ["trips"] })
  }

  async function handleBulkStatusUpdate(payload: {
    status?: TripStatus
    billingStatus?: "controlado" | "liquidado" | "facturado" | "cerrado" | null
  }) {
    if (selectedIds.size === 0) return
    setBulkStatusPending(true)
    try {
      await authorizedFetch(`${API_URL}/api/trips/bulk-status`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ids: [...selectedIds], ...payload }),
      })
      qc.invalidateQueries({ queryKey: ["trips"] })
      setSelectedIds(new Set())
      toast.success(`${selectedIds.size} viaje${selectedIds.size !== 1 ? "s" : ""} actualizados`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo actualizar el estado de los viajes")
    } finally {
      setBulkStatusPending(false)
    }
  }

  async function handleBulkAdvance(hasTravelAdvance: "viaje" | "gnc" | "viaje_gnc" | null) {
    if (selectedIds.size === 0) return
    setBulkStatusPending(true)
    try {
      await authorizedFetch(`${API_URL}/api/trips/bulk-status`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ids: [...selectedIds], hasTravelAdvance }),
      })
      qc.invalidateQueries({ queryKey: ["trips"] })
      setSelectedIds(new Set())
      const label = travelAdvanceLabel(hasTravelAdvance)
      toast.success(`Anticipo «${label}» aplicado a ${selectedIds.size} viaje${selectedIds.size !== 1 ? "s" : ""}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al actualizar anticipo")
    } finally {
      setBulkStatusPending(false)
    }
  }

  async function handleValuationExport() {
    // Use all selected trip IDs directly — works for trips with or without a shipment
    const tripIds = [...selectedIds]
    if (tripIds.length === 0) return
    try {
      const res = await authorizedFetch(
        `${API_URL}/api/trips/export/valuation`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ ids: tripIds }),
        },
      )
      const disposition = res.headers.get("Content-Disposition") ?? ""
      const match       = disposition.match(/filename="([^"]+)"/)
      const filename    = match?.[1] ?? "valorizacion.csv"
      const csvText     = await res.text()
      setTripExportPreview({ csvText, filename })
    } catch (err) {
      console.error("Export failed", err)
    }
  }

  async function handleTripExport() {
    const tripIds = [...selectedIds]
    if (tripIds.length === 0) return
    try {
      const res = await authorizedFetch(
        `${API_URL}/api/trips/export/trip-data`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ ids: tripIds }),
        },
      )
      const disposition = res.headers.get("Content-Disposition") ?? ""
      const match       = disposition.match(/filename="([^"]+)"/)
      const filename    = match?.[1] ?? "datos-de-viaje.csv"
      const csvText     = await res.text()
      setTripExportPreview({ csvText, filename })
    } catch (err) {
      console.error("Export failed", err)
    }
  }

  const toggleAdvanceMut = useMutation({
    mutationFn: async (t: TripRow) => {
      await authorizedFetch(`${API_URL}/api/trips/${t.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hasTravelAdvance: nextTravelAdvance(t.hasTravelAdvance) }),
      })
    },
    onMutate: (t: TripRow) => {
      const next = nextTravelAdvance(t.hasTravelAdvance)
      qc.setQueriesData<{ data: TripRow[]; total: number }>(
        { queryKey: ["trips"], exact: false },
        (old) => old ? { ...old, data: old.data.map(r => r.id === t.id ? { ...r, hasTravelAdvance: next } : r) } : old,
      )
      return { previous: t.hasTravelAdvance }
    },
    onSuccess: (_data, t) => {
      // If the trip belongs to a shipment, refresh the shipments list so the
      // "A" badge on the parent row reflects the new advance value immediately.
      if (t.shipmentId) qc.invalidateQueries({ queryKey: ["shipments"] })
    },
    onError: (_err, t, ctx) => {
      // Roll back on failure
      qc.setQueriesData<{ data: TripRow[]; total: number }>(
        { queryKey: ["trips"], exact: false },
        (old) => old ? { ...old, data: old.data.map(r => r.id === t.id ? { ...r, hasTravelAdvance: ctx?.previous ?? null } : r) } : old,
      )
    },
  })

  const isSaving = createMut.isPending || updateMut.isPending || mobileEditor.isPending

  // ── Interconnect ──────────────────────────────────────────────────────────
  const {
    interconnectTrips,
    interconnectPreview,
    openPreview:        handleOpenInterconnectPreview,
    confirmInterconnect: handleConfirmInterconnect,
    closePreview:       handleCloseInterconnectPreview,
  } = useInterconnect(tripsData ?? [], selectedIds, {
    invalidateKeys: [["trips"]],
    onSuccess: () => setSelectedIds(new Set()),
  })

  // ── Return to base ──────────────────────────────────────────────────────────
  const {
    returnToBaseTrip,
    returnToBasePreview,
    openPreview:          handleOpenReturnToBasePreview,
    confirmReturnToBase:  handleConfirmReturnToBase,
    closePreview:         handleCloseReturnToBasePreview,
  } = useReturnToBase(tripsData ?? [], selectedIds, {
    basePartyId:      appConfig?.trips.basePartyId     ?? "",
    baseCityId:       appConfig?.trips.baseCityId      ?? null,
    baseCityName:     appConfig?.trips.baseCityName    ?? null,
    averageSpeedKmh:  appConfig?.trips.averageSpeedKmh ?? 80,
    invalidateKeys:   [["trips"]],
    onSuccess: () => setSelectedIds(new Set()),
  })

  useEffect(() => {
    if (!pendingReturnToBaseId) return
    if (returnToBaseTrip?.id === pendingReturnToBaseId) {
      setPendingReturnToBaseId(null)
      handleOpenReturnToBasePreview()
    }
  }, [returnToBaseTrip, pendingReturnToBaseId])

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title="Viajes" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 space-y-4">

        {/* ── Toolbar ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-72">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar viaje, envío, chofer, ciudad, remito..."
              className="pl-8"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
          </div>
          {can("write", "trips") && (
            <Button
              onClick={() => { setEditing(null); setEditResources(false); setForm({ ...EMPTY_FORM }); setFormError(null); setSheetOpen(true) }}
              className="gap-2"
            >
              <PlusIcon className="size-4" />
              Nuevo viaje
            </Button>
          )}
        </div>

        {/* ── Filter bar ── */}
        <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">

          {/* Row 1: entity filters + date range + page size */}
          <div className="flex flex-wrap items-center gap-2">

            {/* Estado (activos/eliminados) */}
            <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v as FilterStatus); setPage(1) }}>
              <SelectTrigger className="h-7 w-28 text-xs">
                <SelectValue>
                  {filterStatus === "active" ? "Activos" : filterStatus === "deleted" ? "Eliminados" : "Todos"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="deleted">Eliminados</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>

            <div className="w-px h-5 bg-border shrink-0" />

            {/* Chofer */}
            <div className="w-44">
              <SearchableSelect
                compact
                emptyValue=""
                options={[
                  // Keep selected driver visible in trigger even before the user types
                  ...(filterDriver && !driverFilterOptions.find(d => d.name === filterDriver)
                    ? [{ value: filterDriver, label: filterDriver }]
                    : []),
                  ...driverFilterOptions.map(d => ({ value: d.name, label: d.name })),
                ]}
                value={filterDriver}
                onValueChange={(v) => { setFilterDriver(v); setPage(1) }}
                onSearchChange={onDriverSearchChange}
                placeholder="Todos los choferes"
                searchPlaceholder={driverIsMinLength ? "Buscando..." : "Escribí 3 letras para buscar"}
                emptyLabel={
                  driverInputValue.trim().length > 0 && !driverIsMinLength
                    ? "Escribí al menos 3 letras"
                    : "Sin resultados"
                }
              />
            </div>

            {/* Operadores */}
            {uniqueOperatorNames.length > 0 && (
              <div className="w-44">
                <SearchableSelect
                  multiple
                  compact
                  options={uniqueOperatorNames.map(n => ({ value: n, label: n }))}
                  value={filterOperatorNames}
                  onValueChange={(v) => { setFilterOperatorNames(v); setPage(1) }}
                  placeholder="Todos los operadores"
                  searchPlaceholder="Buscar operador..."
                />
              </div>
            )}

            <div className="w-px h-5 bg-border shrink-0" />

            {/* Facturación */}
            <Select value={filterBilling} onValueChange={(v) => { setFilterBilling(v as BillingStatus | "all"); setPage(1) }}>
              <SelectTrigger className="h-7 w-32 text-xs">
                <SelectValue>
                  {filterBilling === "all" ? "Facturación" : BILLING_STATUS_LABELS[filterBilling]}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {ALL_BILLING_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{BILLING_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Carga asociada */}
            <Select value={filterHasShipment} onValueChange={(v) => { setFilterHasShipment(v as "all" | "with" | "without"); setPage(1) }}>
              <SelectTrigger className="h-7 w-32 text-xs">
                <SelectValue>
                  {filterHasShipment === "all" ? "Con/sin carga" : filterHasShipment === "with" ? "Con carga" : "Sin carga"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="with">Con carga</SelectItem>
                <SelectItem value="without">Sin carga</SelectItem>
              </SelectContent>
            </Select>

            <div className="w-px h-5 bg-border shrink-0" />

            {/* Rango de fechas */}
            <div className="flex items-center gap-1.5 shrink-0">
              <CalendarIcon className="size-3.5 text-muted-foreground shrink-0" />
              <Input
                type="datetime-local"
                value={filterDateFrom}
                onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1) }}
                className="h-7 w-44 text-xs px-2"
                title="Llegada origen desde"
              />
              <span className="text-muted-foreground text-xs">—</span>
              <Input
                type="datetime-local"
                value={filterDateTo}
                onChange={(e) => { setFilterDateTo(e.target.value); setPage(1) }}
                className="h-7 w-44 text-xs px-2"
                title="Llegada origen hasta"
              />
              {(filterDateFrom || filterDateTo) && (
                <button
                  type="button"
                  onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); setPage(1) }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Limpiar fechas"
                >
                  <XIcon className="size-3" />
                </button>
              )}
            </div>

            {/* Page size — right */}
            <div className="flex items-center gap-1 ml-auto shrink-0">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => { setPageSize(size); setPage(1) }}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    pageSize === size
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-muted-foreground"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: trip status chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            {ALL_STATUSES.map((s) => {
              const active = !filterTripStatus || filterTripStatus === s
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => { setFilterTripStatus(prev => prev === s ? "" : s); setPage(1) }}
                  className={cn(
                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity",
                    STATUS_COLORS[s],
                    active ? "opacity-100" : "opacity-30",
                  )}
                >
                  {STATUS_LABELS[s]}
                </button>
              )
            })}
            {filterTripStatus && (
              <button
                type="button"
                onClick={() => { setFilterTripStatus(""); setPage(1) }}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                ver todos
              </button>
            )}
          </div>
        </div>

        {/* ── Selection action bar ── */}
        {selectedIds.size > 0 && (
          <div className="sticky top-0 z-20 flex items-center gap-2 rounded-lg border bg-background/95 backdrop-blur-sm px-3 py-2 shadow-md">
            <span className="flex items-center gap-1.5 shrink-0">
              <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tabular-nums">
                {selectedIds.size}
              </span>
              <span className="text-sm font-medium text-foreground">
                viaje{selectedIds.size !== 1 ? "s" : ""} seleccionado{selectedIds.size !== 1 ? "s" : ""}
              </span>
            </span>

            <div className="w-px self-stretch bg-border mx-1" />

            {can("write", "trips") && (
              <>
                {/* Estado */}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={bulkStatusPending}
                    render={
                      <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs">
                        <CircleCheckIcon className="size-3" />
                        Estado
                        <ChevronDownIcon className="size-3" />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="start" className="w-44">
                    {ALL_STATUSES.map(s => (
                      <DropdownMenuItem
                        key={s}
                        onClick={() => confirmThen(
                          "Cambiar estado",
                          `¿Cambiás el estado de ${selectedIds.size} viaje${selectedIds.size !== 1 ? "s" : ""} a «${STATUS_LABELS[s]}»?`,
                          () => handleBulkStatusUpdate({ status: s }),
                        )}
                      >
                        <span className={cn("size-2 rounded-full shrink-0", STATUS_COLORS[s].split(" ")[0])} />
                        {STATUS_LABELS[s]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Facturación */}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={bulkStatusPending}
                    render={
                      <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs">
                        <ReceiptIcon className="size-3" />
                        Facturación
                        <ChevronDownIcon className="size-3" />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="start" className="w-44">
                    {ALL_BILLING_STATUSES.map(s => (
                      <DropdownMenuItem
                        key={s}
                        onClick={() => confirmThen(
                          "Cambiar facturación",
                          `¿Cambiás la facturación de ${selectedIds.size} viaje${selectedIds.size !== 1 ? "s" : ""} a «${BILLING_STATUS_LABELS[s]}»?`,
                          () => handleBulkStatusUpdate({ billingStatus: s === "pendiente" ? null : s as "controlado" | "liquidado" | "facturado" | "cerrado" }),
                        )}
                      >
                        <span className={cn("size-2 rounded-full shrink-0", BILLING_STATUS_COLORS[s].split(" ")[0])} />
                        {BILLING_STATUS_LABELS[s]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Anticipo */}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={bulkStatusPending}
                    render={
                      <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs">
                        <BanknoteIcon className="size-3" />
                        Anticipo
                        <ChevronDownIcon className="size-3" />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="start" className="w-44">
                    {TRAVEL_ADVANCE_OPTIONS.map(({ value, label }) => (
                      <DropdownMenuItem
                        key={value ?? "no"}
                        onClick={() => confirmThen(
                          "Cambiar anticipo",
                          `¿Cambiás el anticipo de ${selectedIds.size} viaje${selectedIds.size !== 1 ? "s" : ""} a «${label}»?`,
                          () => handleBulkAdvance(value),
                        )}
                      >
                        <span className={cn("size-2 rounded-full shrink-0", travelAdvanceColor(value).split(" ")[0])} />
                        {label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="w-px self-stretch bg-border mx-1" />

                {/* Interconexión */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 h-7 text-xs"
                  disabled={!interconnectTrips}
                  onClick={handleOpenInterconnectPreview}
                  title={!interconnectTrips ? "Seleccioná exactamente 2 viajes del mismo chofer" : "Ver interconexión"}
                >
                  <GitMergeIcon className="size-3" />
                  Interconexión
                </Button>

                {/* Volver a base */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 h-7 text-xs"
                  disabled={!returnToBaseTrip}
                  onClick={handleOpenReturnToBasePreview}
                  title={!returnToBaseTrip
                    ? "Seleccioná exactamente 1 viaje"
                    : `Crear viaje de regreso a base (vel. estimada: ${appConfig?.trips.averageSpeedKmh ?? 80} km/h)`}
                >
                  <HomeIcon className="size-3" />
                  Volver a base
                </Button>

                <div className="w-px self-stretch bg-border mx-1" />
              </>
            )}

            {/* Exportar */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide shrink-0">Exportar:</span>
              <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs px-2 hover:bg-muted" onClick={handleTripExport}>
                <TruckIcon className="size-3" /> Datos de viaje
              </Button>
              <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs px-2 hover:bg-muted" onClick={handleValuationExport}>
                <CalculatorIcon className="size-3" /> Valorización
              </Button>
            </div>

            {/* Cerrar */}
            <Button
              variant="ghost" size="sm" className="h-7 w-7 p-0 ml-auto shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => setSelectedIds(new Set())}
              title="Deseleccionar todo"
            >
              <XIcon className="size-3.5" />
            </Button>
          </div>
        )}

        {/* Table */}
        {serverTotal > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
            <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, serverTotal)} de {serverTotal} resultados</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="p-1 rounded hover:bg-muted disabled:opacity-40 disabled:pointer-events-none"
              >
                <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <span className="min-w-[4rem] text-center tabular-nums">{page} / {totalPages}</span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="p-1 rounded hover:bg-muted disabled:opacity-40 disabled:pointer-events-none"
              >
                <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          </div>
        )}
        <div className="rounded-md border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-9 pr-0">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected }}
                    onChange={toggleSelectAll}
                    className="size-3.5 rounded border-border cursor-pointer accent-primary"
                    title="Seleccionar página"
                  />
                </TableHead>
                <TableHead className="w-14">
                  <button
                    type="button"
                    onClick={() => toggleSort("internalId")}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                  >
                    # Viaje
                    {sortField === "internalId"
                      ? sortDir === "asc" ? <ChevronUpIcon className="size-3" /> : <ChevronDownIcon className="size-3" />
                      : <ChevronsUpDownIcon className="size-3 opacity-40" />}
                  </button>
                </TableHead>
                <TableHead className="w-20">Carga</TableHead>
                <TableHead className="w-16">Móvil</TableHead>
                <TableHead>Camión / Semi</TableHead>
                <TableHead>Chofer</TableHead>
                <TableHead>Origen → Destino</TableHead>
                <TableHead className="w-32">
                  <button
                    type="button"
                    onClick={() => toggleSort("departureAt")}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                  >
                    Ll. proy. origen
                    {sortField === "departureAt"
                      ? sortDir === "asc" ? <ChevronUpIcon className="size-3" /> : <ChevronDownIcon className="size-3" />
                      : <ChevronsUpDownIcon className="size-3 opacity-40" />}
                  </button>
                </TableHead>
                <TableHead className="w-32">Salida / Llegada</TableHead>
                <TableHead className="w-36">Estado</TableHead>
                <TableHead className="w-12 text-center">Ant.</TableHead>
                {can("write", "trips") && <TableHead className="w-9" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={can("write", "trips") ? 12 : 11} className="h-24 text-center text-muted-foreground">
                    Cargando viajes...
                  </TableCell>
                </TableRow>
              ) : tripsData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={can("write", "trips") ? 12 : 11} className="h-24 text-center text-muted-foreground">
                    No se encontraron viajes.
                  </TableCell>
                </TableRow>
              ) : tripsData.map((t) => (
                <TableRow key={t.id} className={cn(t.deletedAt && "opacity-50 italic", selectedIds.has(t.id) && "bg-muted/50")}>
                  {/* Checkbox */}
                  <TableCell className="pr-0">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(t.id)}
                      onChange={() => toggleSelect(t.id)}
                      className="size-3.5 rounded border-border cursor-pointer accent-primary"
                    />
                  </TableCell>

                  {/* # Viaje */}
                  <TableCell>
                    <span className="font-mono text-xs font-semibold text-primary">
                      #{t.internalId}
                    </span>
                  </TableCell>

                  {/* Carga (envío) */}
                  <TableCell>
                    {t.shipmentInternalId != null ? (
                      <Link
                        to={`/shipments/${t.shipmentId}`}
                        className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded hover:bg-muted/70 transition-colors"
                      >
                        #{t.shipmentInternalId}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground/30 text-xs">—</span>
                    )}
                  </TableCell>

                  {/* Móvil */}
                  <TableCell>
                    {t.truckUnitNumber != null ? (
                      <span className="font-mono text-xs font-semibold">
                        M{t.truckUnitNumber}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/30 text-xs">—</span>
                    )}
                  </TableCell>

                  {/* Camión / Semi */}
                  <TableCell>
                    {t.truckLicensePlate ? (
                      <div className="flex items-center gap-1.5 text-sm font-medium leading-tight">
                        <span className="font-mono">{t.truckLicensePlate}</span>
                      </div>
                    ) : null}
                    {(t.trailerLicensePlate || t.trailerUnitNumber != null) ? (
                      <div className="flex items-center gap-1.5 text-sm leading-tight text-muted-foreground">
                        {t.trailerUnitNumber != null && (
                          <span className="font-mono text-[10px] bg-muted/60 px-1 py-0.5 rounded">
                            S{t.trailerUnitNumber}
                          </span>
                        )}
                        {t.trailerLicensePlate && (
                          <span className="font-mono text-xs">{t.trailerLicensePlate}</span>
                        )}
                      </div>
                    ) : null}
                    {!t.truckLicensePlate && !t.trailerLicensePlate && (
                      <span className="text-muted-foreground/30 text-xs">—</span>
                    )}
                  </TableCell>

                  {/* Chofer */}
                  <TableCell>
                    {t.driverName ? (
                      <div className="flex items-center gap-1.5">
                        <Avatar className="size-5">
                          <AvatarFallback
                            className="text-white text-[9px] font-semibold"
                            style={{ backgroundColor: avatarColor(t.driverName) }}
                          >
                            {avatarInitials(t.driverName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs text-muted-foreground">{t.driverName}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground/30 text-xs">—</span>
                    )}
                  </TableCell>

                  {/* Origen → Destino + KM */}
                  <TableCell>
                    {(t.originCityName || t.destinationCityName) ? (
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1 text-sm">
                          <span>{t.originCityName ?? "—"}</span>
                          <ArrowRightIcon className="size-3 shrink-0 text-muted-foreground" />
                          <span>{t.destinationCityName ?? "—"}</span>
                        </div>
                        {t.routeKm != null ? (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <RouteIcon className="size-3 shrink-0" />
                            <span className="tabular-nums">{t.routeKm.toLocaleString("es-AR")} km</span>
                          </div>
                        ) : (t.originCityName && t.destinationCityName) ? (
                          <div className="flex items-center gap-1 text-xs text-amber-500">
                            <TriangleAlertIcon className="size-3 shrink-0" />
                            <span>Sin ruta registrada</span>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-muted-foreground/30 text-xs">—</span>
                    )}
                  </TableCell>

                  {/* Llegada proyectada a origen */}
                  <TableCell className="text-xs tabular-nums">
                    {t.scheduledOriginArrivalAt != null ? (
                      <span className="text-muted-foreground">{fmtDate(t.scheduledOriginArrivalAt)}</span>
                    ) : t.departureAt != null ? (
                      <span className="text-muted-foreground/40 italic" title="Fecha de salida del viaje (sin llegada proyectada)">{fmtDate(t.departureAt)}</span>
                    ) : (
                      <span className="text-muted-foreground/30">—</span>
                    )}
                  </TableCell>

                  {/* Salida / Llegada */}
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    <div className="flex flex-col gap-0.5 leading-tight">
                      <span>{fmtDate(t.departureAt)}</span>
                      <span className="text-muted-foreground/60">{fmtDate(t.arrivalAt)}</span>
                    </div>
                  </TableCell>

                  {/* Estado + Facturación */}
                  <TableCell>
                    <div className="flex flex-col gap-1 items-start">
                      {/* Badge estado operativo — clickeable si puede editar */}
                      {can("write", "trips") && !t.deletedAt ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger render={
                            <button className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity",
                              STATUS_COLORS[t.status],
                            )}>
                              {STATUS_LABELS[t.status]}
                            </button>
                          } />
                          <DropdownMenuContent align="start" className="w-44">
                            {ALL_STATUSES.filter(s => s !== t.status).map(s => (
                              <DropdownMenuItem
                                key={s}
                                onClick={() => confirmThen(
                                  "Cambiar estado",
                                  `¿Cambiás el estado del viaje #${t.internalId} a «${STATUS_LABELS[s]}»?`,
                                  async () => { await patchTripStatus(t.id, { status: s }) },
                                )}
                              >
                                <span className={cn("size-2 rounded-full shrink-0", STATUS_COLORS[s].split(" ")[0])} />
                                {STATUS_LABELS[s]}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap",
                          STATUS_COLORS[t.status],
                        )}>
                          {STATUS_LABELS[t.status]}
                        </span>
                      )}
                      {/* Badge facturación — clickeable si puede editar */}
                      {(() => {
                        const bs: BillingStatus = t.billingStatus ?? "pendiente"
                        const badge = (
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap",
                            BILLING_STATUS_COLORS[bs],
                          )}>
                            {BILLING_STATUS_LABELS[bs]}
                          </span>
                        )
                        if (!can("write", "trips") || t.deletedAt) return bs === "pendiente" ? null : badge
                        return (
                          <DropdownMenu>
                            <DropdownMenuTrigger render={
                              <button className={cn(
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity",
                                BILLING_STATUS_COLORS[bs],
                              )}>
                                {BILLING_STATUS_LABELS[bs]}
                              </button>
                            } />
                            <DropdownMenuContent align="start" className="w-44">
                              {ALL_BILLING_STATUSES.filter(s => s !== bs).map(s => (
                                <DropdownMenuItem
                                  key={s}
                                  onClick={() => confirmThen(
                                    "Cambiar facturación",
                                    `¿Cambiás la facturación del viaje #${t.internalId} a «${BILLING_STATUS_LABELS[s]}»?`,
                                    async () => { await patchTripStatus(t.id, { billingStatus: s === "pendiente" ? null : s as "controlado" | "liquidado" | "facturado" | "cerrado" }) },
                                  )}
                                >
                                  <span className={cn("size-2 rounded-full shrink-0", BILLING_STATUS_COLORS[s].split(" ")[0])} />
                                  {BILLING_STATUS_LABELS[s]}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )
                      })()}
                    </div>
                  </TableCell>

                  {/* Anticipo de viaje */}
                  <TableCell className="text-center">
                    <button
                      type="button"
                      onClick={() => !t.deletedAt && toggleAdvanceMut.mutate(t)}
                      disabled={toggleAdvanceMut.isPending || !!t.deletedAt}
                      title="Click para cambiar"
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors cursor-pointer disabled:opacity-50",
                        travelAdvanceColor(t.hasTravelAdvance),
                        travelAdvanceHover(t.hasTravelAdvance),
                      )}
                    >
                      {travelAdvanceLabel(t.hasTravelAdvance)}
                    </button>
                  </TableCell>

                  {/* Acciones */}
                  {can("write", "trips") && (
                    <TableCell>
                      {t.deletedAt ? (
                        <Button
                          variant="ghost" size="icon-sm"
                          onClick={() => restoreMut.mutate(t.id)}
                          disabled={restoringId === t.id}
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          title="Restaurar"
                        >
                          <RestoreIcon className="size-3.5" />
                        </Button>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger render={
                            <Button variant="ghost" size="icon-sm" title="Acciones">
                              <MoreVerticalIcon className="size-4" />
                            </Button>
                          } />
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem onClick={() => openEdit(t)}>
                              <PencilIcon className="size-3.5" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedIds(new Set([t.id]))
                              setPendingReturnToBaseId(t.id)
                            }}>
                              <HomeIcon className="size-3.5" />
                              Volver a base
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => { setDeleting(t); setDeleteOpen(true) }}
                            >
                              <Trash2Icon className="size-3.5" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={serverTotal}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* ── Create / Edit sheet ── */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet() }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar viaje" : "Nuevo viaje"}</SheetTitle>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">

            {/* Estado de facturación */}
            <div className="space-y-1">
              <Label>Estado de facturación</Label>
              <Select
                value={form.billingStatus ?? "pendiente"}
                onValueChange={(v) =>
                  setForm(f => ({ ...f, billingStatus: v === "pendiente" ? null : v as "controlado" | "liquidado" | "facturado" | "cerrado" }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALL_BILLING_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{BILLING_STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estado */}
            <div className="grid gap-1.5">
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => {
                const newStatus = v as TripStatus
                setForm(f => {
                  const next: FormState = { ...f, status: newStatus }
                  const nowLocal = isoToLocal(new Date().toISOString())
                  if (f.status === "pendiente" && newStatus === "en_curso" && !f.departureAt) {
                    next.departureAt = nowLocal
                  }
                  if (f.status === "en_curso" && newStatus === "completado" && !f.arrivalAt) {
                    next.arrivalAt = nowLocal
                  }
                  return next
                })
              }}>
                <SelectTrigger>
                  <SelectValue>{STATUS_LABELS[form.status]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {ALL_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Móvil — readonly snapshot when editing, selector when creating */}
            {editing ? (
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between">
                  <Label>Recursos del viaje</Label>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setEditResources(v => !v)}
                  >
                    {editResources ? "Cancelar cambio" : "Cambiar recursos"}
                  </button>
                </div>

                {!editResources && (
                  <div className="rounded-md border border-input bg-muted/40 px-3 py-2.5 text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-14 shrink-0">Chofer</span>
                      <span className="font-medium">{editing.driverName ?? <span className="text-muted-foreground italic">Sin chofer</span>}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-14 shrink-0">Camión</span>
                      <span className="font-medium">
                        {editing.truckLicensePlate
                          ? <>{editing.truckLicensePlate}{editing.truckUnitNumber != null && <span className="text-muted-foreground ml-1.5">#{editing.truckUnitNumber}</span>}</>
                          : <span className="text-muted-foreground italic">Sin camión</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-14 shrink-0">Semi</span>
                      <span className="font-medium">
                        {editing.trailerLicensePlate
                          ? <>{editing.trailerLicensePlate}{editing.trailerUnitNumber != null && <span className="text-muted-foreground ml-1.5">#{editing.trailerUnitNumber}</span>}</>
                          : <span className="text-muted-foreground italic">Sin semi</span>}
                      </span>
                    </div>
                  </div>
                )}

                {editResources && (
                  <div className="rounded-md border border-input px-3 py-3 space-y-3">
                    <div className="grid gap-1.5">
                      <Label className="text-xs text-muted-foreground">Chofer</Label>
                      <SearchableSelect
                        options={driverSelectOptions}
                        value={form.driverId || "__none__"}
                        onValueChange={(v) => setForm(f => ({ ...f, driverId: v === "__none__" ? "" : v }))}
                        placeholder="Sin chofer"
                        searchPlaceholder="Buscar chofer..."
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-xs text-muted-foreground">Camión</Label>
                      <SearchableSelect
                        options={truckSelectOptions}
                        value={form.truckId || "__none__"}
                        onValueChange={(v) => setForm(f => ({ ...f, truckId: v === "__none__" ? "" : v }))}
                        placeholder="Sin camión"
                        searchPlaceholder="Buscar patente..."
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-xs text-muted-foreground">Semi</Label>
                      <SearchableSelect
                        options={trailerSelectOptions}
                        value={form.trailerId || "__none__"}
                        onValueChange={(v) => setForm(f => ({ ...f, trailerId: v === "__none__" ? "" : v }))}
                        placeholder="Sin semi"
                        searchPlaceholder="Buscar patente..."
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid gap-1.5">
                <Label>Móvil</Label>
                <SearchableSelect
                  options={mobileEditor.mobileSelectOptions}
                  value={form.mobileId || "__none__"}
                  onValueChange={(v) => {
                    const newId = v === "__none__" ? "" : v
                    setForm(f => ({ ...f, mobileId: newId }))
                    mobileEditor.onMobileSelected(newId)
                  }}
                  placeholder="Sin asignar"
                  searchPlaceholder="Buscar por patente o chofer..."
                />
                <MobileEditorPanel mobileId={form.mobileId} editor={mobileEditor} />
              </div>
            )}

            {/* Ciudad origen */}
            <div className="grid gap-1.5">
              <Label>Ciudad de origen</Label>
              <SearchableSelect
                options={citySelectOptions}
                value={form.originCityId || "__none__"}
                onValueChange={(v) => setForm(f => ({ ...f, originCityId: v === "__none__" ? "" : v }))}
                placeholder="Sin ciudad"
                searchPlaceholder="Buscar ciudad..."
              />
            </div>

            {/* Ciudad destino */}
            <div className="grid gap-1.5">
              <Label>Ciudad de destino</Label>
              <SearchableSelect
                options={citySelectOptions}
                value={form.destinationCityId || "__none__"}
                onValueChange={(v) => setForm(f => ({ ...f, destinationCityId: v === "__none__" ? "" : v }))}
                placeholder="Sin ciudad"
                searchPlaceholder="Buscar ciudad..."
              />
            </div>

            {/* Salida */}
            <div className="grid gap-1.5">
              <Label>Salida real</Label>
              <Input
                type="datetime-local"
                value={form.departureAt}
                onChange={(e) => setForm(f => ({ ...f, departureAt: e.target.value }))}
              />
            </div>

            {/* Llegada */}
            <div className="grid gap-1.5">
              <Label>Llegada real</Label>
              <Input
                type="datetime-local"
                value={form.arrivalAt}
                onChange={(e) => setForm(f => ({ ...f, arrivalAt: e.target.value }))}
              />
            </div>

            {/* Notas */}
            <div className="grid gap-1.5">
              <Label>Notas</Label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Observaciones del viaje..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              />
            </div>

            {/* Anticipo de viaje */}
            <div className="grid gap-1.5">
              <Label>Anticipo de viaje</Label>
              <Select
                value={form.hasTravelAdvance ?? "no"}
                onValueChange={(v) => setForm(f => ({
                  ...f,
                  hasTravelAdvance: v === "no" ? null : v as "viaje" | "gnc" | "viaje_gnc",
                }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="viaje">Viaje</SelectItem>
                  <SelectItem value="gnc">GNC</SelectItem>
                  <SelectItem value="viaje_gnc">Viaje + GNC</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear viaje"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <MobileEditorConfirmDialog
        editor={mobileEditor}
        onConfirmed={(driverId, trailerId) => {
          mobileEditor.closeConfirm()
          doSave({ ...form, driverId, trailerId })
        }}
      />

      {/* ── Delete dialog ── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar viaje</DialogTitle>
            <DialogDescription>
              ¿Eliminás el viaje
              {deleting?.shipmentInternalId != null ? <> del envío <strong>#{deleting.shipmentInternalId}</strong></> : " independiente"}
              {deleting?.truckLicensePlate ? ` — ${deleting.truckLicensePlate}` : ""}?
              Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ── Export Preview Modal ── */}
      <Dialog open={!!tripExportPreview} onOpenChange={(open) => { if (!open) setTripExportPreview(null) }}>
        <DialogContent className="max-w-5xl w-full flex flex-col" style={{ maxHeight: "85vh" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DownloadIcon className="size-4" />
              Preview de exportación
            </DialogTitle>
            <DialogDescription className="font-mono text-xs truncate">
              {tripExportPreview?.filename}
            </DialogDescription>
          </DialogHeader>
          {tripExportPreview && (() => {
            const lines = tripExportPreview.csvText.trim().split("\n")
            const parseRow = parseCSVRow
            // Cells marked with this sentinel by the API are missing required data.
            // The row is highlighted amber and excluded from the downloaded file.
            const MISSING_SENTINEL = "\x00MISSING\x00"
            const isMissingCell  = (cell: string) => cell.startsWith(MISSING_SENTINEL)
            const displayCell    = (cell: string) => isMissingCell(cell) ? cell.slice(MISSING_SENTINEL.length) : cell
            const isProblematicRow = (row: string[]) => row.some(isMissingCell)
            // The CSV has 3 header lines: TITLE, NUM, HEADER (column names).
            // lines[0..2] = metadata, lines[3+] = data rows.
            const HEADER_LINES = 3
            const headers  = parseRow(lines[HEADER_LINES - 1] ?? "")  // column names row
            const metaRows = lines.slice(0, HEADER_LINES - 1).map(parseRow) // TITLE + NUM
            const allRows  = lines.slice(HEADER_LINES).map(parseRow)         // data only
            const okRows   = allRows.filter(r => !isProblematicRow(r))
            const badCount = allRows.length - okRows.length
            // CSV to download: all 3 header lines + clean data rows
            const escape   = (c: string) => escapeCSVCell(displayCell(c))
            const cleanCsvText = [
              ...lines.slice(0, HEADER_LINES),
              ...okRows.map(r => r.map(escape).join(",")),
            ].join("\n")
            return (
              <>
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Button size="sm" className="gap-2" onClick={() => {
                    const blob = new Blob([cleanCsvText], { type: "text/csv;charset=utf-8;" })
                    const url  = URL.createObjectURL(blob)
                    const a    = document.createElement("a")
                    a.href = url; a.download = tripExportPreview.filename; a.click()
                    URL.revokeObjectURL(url)
                  }}>
                    <DownloadIcon className="size-4" /> Descargar CSV
                  </Button>
                  <Button size="sm" variant="outline" className="gap-2" onClick={() => {
                    const esc = escapeHTML
                    let html = `<!DOCTYPE html><html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"></head><body><table>`
                    html += "<thead><tr>" + headers.map(h => `<th>${esc(h)}</th>`).join("") + "</tr></thead>"
                    html += "<tbody>" + okRows.map(r => `<tr>${r.map(c => `<td>${esc(displayCell(c))}</td>`).join("")}</tr>`).join("") + "</tbody>"
                    html += "</table></body></html>"
                    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" })
                    const url  = URL.createObjectURL(blob)
                    const a    = document.createElement("a")
                    a.href = url; a.download = tripExportPreview.filename.replace(/\.csv$/i, ".xls"); a.click()
                    URL.revokeObjectURL(url)
                  }}>
                    <DownloadIcon className="size-4" /> Descargar XLS (97–2003)
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {okRows.length} fila{okRows.length !== 1 ? "s" : ""}
                    {badCount > 0 && (
                      <span className="ml-2 text-amber-600 font-medium">
                        · {badCount} con problema (excluida{badCount !== 1 ? "s" : ""})
                      </span>
                    )}
                  </span>
                </div>
                <div className="overflow-auto flex-1 rounded border">
                  <table className="text-xs w-full border-collapse">
                    <thead className="sticky top-0 bg-muted z-10">
                      {metaRows.map((row, ri) => (
                        <tr key={`meta-${ri}`}>
                          {row.map((h, i) => (
                            <th key={i} className="border-b border-r px-2 py-1.5 text-left whitespace-nowrap text-muted-foreground/60 text-[10px] last:border-r-0">
                              {h}
                            </th>
                          ))}
                        </tr>
                      ))}
                      <tr>
                        {headers.map((h, i) => (
                          <th key={i} className="border-b border-r px-2 py-1.5 text-left font-semibold whitespace-nowrap text-muted-foreground last:border-r-0">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allRows.map((row, ri) => {
                        const bad = isProblematicRow(row)
                        return (
                          <tr key={ri} className={bad ? "bg-amber-50" : ri % 2 === 0 ? "bg-background" : "bg-muted/30"}>
                            {row.map((cell, ci) => {
                              const cellMissing = isMissingCell(cell)
                              return (
                                <td key={ci} className={cn(
                                  "border-b border-r px-2 py-1 whitespace-nowrap last:border-r-0 font-mono",
                                  cellMissing ? "bg-red-100 text-red-700 font-semibold" : bad ? "text-amber-700" : "",
                                )}>
                                  {displayCell(cell)}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Return to base dialog ── */}
      <ReturnToBaseDialog
        preview={returnToBasePreview}
        onConfirm={handleConfirmReturnToBase}
        onCancel={handleCloseReturnToBasePreview}
      />

      {/* ── Interconnect trip preview dialog ── */}
      <InterconnectDialog
        preview={interconnectPreview}
        onConfirm={handleConfirmInterconnect}
        onCancel={handleCloseInterconnectPreview}
      />

      {/* ── Confirm status change dialog ── */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null) }}>
        <DialogContent showCloseButton={false} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{confirmDialog?.title}</DialogTitle>
            <DialogDescription>{confirmDialog?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancelar</Button>
            <Button
              onClick={async () => {
                const fn = confirmDialog?.onConfirm
                setConfirmDialog(null)
                await fn?.()
              }}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
